import XIcon from "@mui/icons-material/Close"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Slide,
  Stack,
  Typography,
} from "@mui/material"
import { DialogProps } from "../types"
import { Event, EventImage, ImageOrientationEnum } from "@/common/types"
import dayjs from "dayjs"
import useGetEvent from "@/hooks/api/events/useGetEvent"
import shareIcon from "@/components/mapBox/images/copy_icon.png"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import Flickity, { FlickityOptions } from "react-flickity-component"
import "flickity/css/flickity.css"
import React, { useState, useEffect } from "react"
import { TransitionProps } from "@mui/material/transitions"
import useIsMobile from "@/hooks/useIsMobile"
import MobileLargeEventDialog from "./MobileLargeEventDialog"
import ReactGA from "react-ga4"
import { useSnackbar } from "@/contexts/SnackbarContext"
import { useNavigate } from "react-router-dom"
import useFavoriteEvent from "@/hooks/api/events/useFavoriteEvent"
import useDialogState from "@/hooks/useDialogState"
// import EventCardDialog from "./EventCardDialog"
import LoginDialog from "./LoginDialog"
import { useGetMe } from "@/hooks/useGetMe"
import { HeartIcon } from "@/common/images/HeartIcon"
import { HeartFilledIcon } from "@/common/images/HeartFilledIcon"

interface LargeEventDialogProps extends DialogProps {
  eventId: number
  calendarDate?: string
}

const likeAndShareButtonStyling = {
  //   backgroundColor: "white",
  color: "white",
  width: "42px",
  height: "42px",
  padding: ".5rem",
  paddingY: "0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: ".2rem",
  marginRight: ".2rem",
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<unknown, any>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />
})
const MobileTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<unknown, any>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

function Carousel({ images }: { images: EventImage[] }) {
  const flickityOptions: FlickityOptions = {
    initialIndex: 0,
    cellAlign: "left",
    groupCells: false,
    contain: true,
  }

  const sortedImages = images.sort((image1, image2) => {
    if (image1.orientation !== image2.orientation) {
      return image1.orientation === ImageOrientationEnum.HORIZONTAL ? -1 : 1
    }
    return image1.image_order - image2.image_order
  })

  return (
    <div style={{ position: "relative", overflow: "hidden" }}>
      <Flickity
        className={"carousel"} // default ''
        elementType={"div"} // default 'div'
        options={flickityOptions} // takes flickity options {}
        disableImagesLoaded={false} // default false
        reloadOnUpdate // default false
        static // default false
      >
        {sortedImages.map((image, idx) => {
          if (idx === 0) {
            return (
              <div key={image.event_image_id} style={{ paddingLeft: "100px" }}>
                <img
                  key={image.event_image_id}
                  src={image.image_url}
                  alt="Event image"
                  width="240px"
                  height="240px"
                  style={{
                    marginRight: "40px",
                    borderRadius: "12px",
                    objectFit: "cover",
                  }}
                />
              </div>
            )
          } else {
            return (
              <img
                key={image.event_image_id}
                src={image.image_url}
                alt="Event image"
                width="240px"
                height="240px"
                style={{
                  marginRight: "40px",
                  borderRadius: "12px",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            )
          }
        })}
      </Flickity>
    </div>
  )
}

export default function LargeEventDialog({
  isOpen,
  closeDialog,
  eventId,
  calendarDate,
}: LargeEventDialogProps) {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const { data: me, isLoading: userIsLoading } = useGetMe()
  const { showSnackbar } = useSnackbar()
  // const { openDialog, ...dialogProps } = useDialogState({})
  const {
    openDialog: openLoginDialog,
    closeDialog: closeLoginDialog,
    isOpen: loginIsOpen,
  } = useDialogState({})
  const { mutateAsync: favoriteEvent } = useFavoriteEvent()
  const { data: event, isLoading } = useGetEvent(eventId)

  // Track event view when dialog opens and event is loaded
  useEffect(() => {
    if (isOpen && event && !isLoading) {
      const eventData = event.data as Event
      ReactGA.event({
        category: "Event",
        action: "View event",
        label: `${eventData.title} | Event ID: ${eventData.event_id} | Event Type: ${eventData.event_types?.[0]?.human_name || "Unknown"} | User: ${me?.id ?? "guest"}`,
        value: eventData.event_id,
      })
    }
  }, [isOpen, event, isLoading, me])

  function handleClose() {
    closeDialog()
    navigate(location.pathname, { unstable_viewTransition: true })
  }

  const copyToClipBoard = async (eventId: number) => {
    try {
      const dateParam = window.location.pathname.includes("calendar")
        ? `&date=${calendarDate}`
        : ""

      const shareUrl = `${window.location.origin}${location.pathname}?eventId=${eventId}&details=true${dateParam}`
      await navigator.clipboard.writeText(shareUrl)
      showSnackbar("Copied link to event!", "success")
    } catch (err) {
      console.error("could not copy", err)
    }
  }

  const handleActionClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation()
    ReactGA.event({
      category: "Event",
      action: "Click action url",
      label: eventData.title,
      value: eventData.event_id,
    })

    setTimeout(() => {
      window.open(eventData.action_url, "_blank", "noopener,noreferrer")
    }, 300)
  }

  if (isLoading || userIsLoading) {
    return <>Loading...</>
  }
  const eventData = event.data as Event

  const handleFavoriteEvent = () => {
    favoriteEvent(Number(eventData.event_id)).then((res) => {
      if (res.message === "Event favorited") {
        showSnackbar("Event Favorited", "success")
      } else {
        showSnackbar("Event removed from favorites", "error")
      }
    })
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      TransitionComponent={isMobile ? MobileTransition : Transition}
      fullWidth
      PaperProps={{
        className: "large-event-dialog",
      }}
      sx={{
        minWidth: isMobile ? "90%" : "50%",
        zIndex: 9998,
        maxHeight: isMobile ? "100%" : "90%",
        "& .MuiDialog-paper": isMobile
          ? {
              position: "absolute",
              bottom: 0,
              margin: 0,
              paddingTop: 1,
              backgroundColor: "#2C2C2C",
              // left: 0,
              // right: 0,
              maxHeight: "90%",
              overflowY: "visible",
              width: "90%",
              maxWidth: "90%",
            }
          : {
              position: "absolute",
              overflow: "visible",
              top: 88,
              width: "65%",
              maxWidth: "65%",
            },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "transparent",
          },
        },
      }}
    >
      {isMobile ? (
        <MobileLargeEventDialog
          eventData={eventData}
          handleClose={handleClose}
          calendarDate={calendarDate}
        />
      ) : (
        <>
          <DialogContent
            sx={{ backgroundColor: "#2C2C2C", color: "white", padding: 4 }}
          >
            {/* Header */}
            <Box
              display="flex"
              flexGrow={1}
              alignContent="start"
              sx={{
                color: "white",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <Typography fontSize={20}>
                {dayjs(eventData.event_start_timestamp).format("MMM D, h:mmA")}
              </Typography>
              <Stack
                direction="row"
                gap={1}
                sx={{
                  right: 0,
                  alignItems: "start",
                  justifyContent: "right",
                  padding: ".3rem",
                  paddingY: "0",
                  margin: ".3rem",
                  marginY: "0",
                  zIndex: "+5",
                }}
              >
                <IconButton
                  style={likeAndShareButtonStyling}
                  onClick={(e) => {
                    e.stopPropagation()
                    copyToClipBoard(eventData.event_id)
                  }}
                >
                  <img
                    src={shareIcon}
                    alt="Share event"
                    style={{
                      width: "26px",
                      height: "26px",
                      filter:
                        "invert(100%) sepia(100%) saturate(0%) hue-rotate(288deg) brightness(102%) contrast(102%)",
                    }}
                  />
                </IconButton>
                <IconButton
                  style={likeAndShareButtonStyling}
                  onClick={() => {
                    if (me.length) {
                      handleFavoriteEvent()
                    } else {
                      openLoginDialog({})
                    }
                  }}
                >
                  {eventData.favorited ? <HeartFilledIcon /> : <HeartIcon />}
                </IconButton>
              </Stack>
            </Box>
            <Stack gap={1} pb={6}>
              <Typography variant="h2" fontSize={40}>
                {eventData.title}
              </Typography>
              <Typography fontSize={24}>{eventData.location_title}</Typography>
            </Stack>
            <Stack sx={{ color: "white", paddingBottom: 4 }}>
              <Typography fontWeight={300} sx={{ lineHeight: 1.5 }}>
                {descriptionExpanded
                  ? eventData.description
                  : `${eventData.description.slice(0, 90)}...`}
                {descriptionExpanded ? (
                  <span
                    style={{
                      display: "block", // Ensures it's on a new line
                      color: "#7CFFD0",
                      cursor: "pointer",
                      textDecoration: "underline",
                      marginTop: "5px", // Adds space for better readability
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setDescriptionExpanded(false)
                    }}
                  >
                    click to read less
                  </span>
                ) : (
                  <span
                    style={{
                      color: "#7CFFD0",
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontWeight: 300,
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      setDescriptionExpanded(true)
                    }}
                  >
                    click to view more
                  </span>
                )}
              </Typography>
              <Typography fontStyle="italic" paddingY={2}>
                {eventData.price}
              </Typography>
              <Stack direction="row" gap={2}>
                <Button
                  variant="contained"
                  sx={{ backgroundColor: "#147BE1", borderRadius: 8 }}
                  onClick={(e) => handleActionClick(e)}
                >
                  {eventData.button_label}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    // openDialog({})
                    showSnackbar("Collect Cards Feature Coming Soon!", "info")
                  }}
                  startIcon={<LockOutlinedIcon sx={{ color: "#318200" }} />}
                  sx={{
                    backgroundColor: "#EAFCDE",
                    borderRadius: 8,
                    color: "#318200",
                    "&.MuiButtonBase-root:hover": {
                      bgcolor: "transparent",
                    },
                  }}
                >
                  Collect Card
                </Button>
              </Stack>
            </Stack>
            {eventData.event_images && (
              <Carousel images={eventData.event_images} />
            )}
          </DialogContent>
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              bottom: "-20px", // Adjust as needed
              right: "-20px", // Adjust as needed
              backgroundColor: "black",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              },
            }}
          >
            <XIcon />
          </IconButton>
        </>
      )}
      {/* <EventCardDialog {...dialogProps} /> */}
      <LoginDialog
        closeDialog={closeLoginDialog}
        isOpen={loginIsOpen}
        message="Log in to favorite events!"
      />
    </Dialog>
  )
}
